import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import { FiExternalLink, FiRefreshCw, FiSave, FiVideo } from "react-icons/fi";

const isYouTubeUrl = (value) => {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    return url.protocol === "https:" && ["youtube.com", "m.youtube.com", "youtu.be"].includes(host);
  } catch (_) {
    return false;
  }
};

const TutorialVideoManager = () => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const loadVideo = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/api/v1/admin/tutorial-video");
      setYoutubeUrl(response.data?.data?.youtubeUrl || "");
    } catch (error) {
      toast({ title: "Unable to load tutorial video", description: error.response?.data?.message || "Please try again.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canPreview = useMemo(() => youtubeUrl && isYouTubeUrl(youtubeUrl), [youtubeUrl]);

  const saveVideo = async () => {
    const value = youtubeUrl.trim();
    if (value && !isYouTubeUrl(value)) {
      toast({ title: "Invalid YouTube link", description: "Use an HTTPS youtube.com or youtu.be video link.", status: "warning", duration: 4000, isClosable: true });
      return;
    }

    try {
      setIsSaving(true);
      const response = await axios.put("/api/v1/admin/tutorial-video", { youtubeUrl: value });
      setYoutubeUrl(response.data?.data?.youtubeUrl || "");
      toast({ title: value ? "Tutorial video saved" : "Tutorial video removed", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: "Unable to save tutorial video", description: error.response?.data?.message || "Please try again.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="space-y-6" aria-labelledby="tutorial-video-heading">
      <div className="rounded-2xl border-2 border-[var(--yellow)]/20 bg-gradient-to-br from-gray-900 via-gray-800 to-black p-5 shadow-lg">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 id="tutorial-video-heading" className="flex items-center gap-2 text-2xl font-bold text-[var(--yellow)]"><FiVideo /> App Tutorial Video</h2>
            <p className="mt-1 text-sm text-[color:var(--yellow)/0.8]">Paste the YouTube guide that users can open to learn how to use the app.</p>
          </div>
          <button type="button" onClick={loadVideo} disabled={isLoading || isSaving} className="flex items-center justify-center gap-2 rounded-md border border-[var(--yellow)]/40 px-4 py-2 text-sm font-medium text-[var(--yellow)] transition hover:bg-[var(--yellow)]/10 disabled:cursor-not-allowed disabled:opacity-50"><FiRefreshCw className={isLoading ? "animate-spin" : ""} /> Refresh</button>
        </div>

        <div className="mt-6 max-w-3xl">
          <label htmlFor="tutorial-video-url" className="mb-2 block text-sm font-semibold text-[var(--yellow)]">YouTube video link</label>
          <input id="tutorial-video-url" type="url" value={youtubeUrl} onChange={(event) => setYoutubeUrl(event.target.value)} placeholder="https://www.youtube.com/watch?v=..." disabled={isLoading || isSaving} className="w-full rounded-md border border-[var(--yellow)]/30 bg-black/40 px-4 py-3 text-white outline-none placeholder:text-gray-500 focus:border-[var(--yellow)] disabled:opacity-50" />
          <p className="mt-2 text-xs text-gray-400">Supported: youtube.com and youtu.be HTTPS links. Clear the field and save to remove the video.</p>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button type="button" onClick={saveVideo} disabled={isLoading || isSaving} className="flex items-center gap-2 rounded-md bg-[var(--yellow)] px-4 py-2 font-semibold text-[var(--black)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"><FiSave /> {isSaving ? "Saving..." : "Save video"}</button>
          {canPreview && <a href={youtubeUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-md border border-[var(--yellow)]/40 px-4 py-2 font-medium text-[var(--yellow)] hover:bg-[var(--yellow)]/10"><FiExternalLink /> Open preview</a>}
        </div>
      </div>
    </section>
  );
};

export default TutorialVideoManager;
